/**
 * カスタム項目の台帳（asset_fields）。
 *
 * 項目は全体で1つの台帳に持ち、カテゴリには「割り当て」だけを行う（カテゴリ設定から）。
 * key は機械IDで作成後は変えられない（値はこのキーで保存される）。表示名は自由に変えられる。
 * 型も変えられない（別のキーで作り直す）。選択肢はコード（自動採番）と表示名の組で、
 * 表示名の変更は保存済みの値に影響しない。
 *
 * Box に関係しないので PostgREST に直接書く（RLS が管理権限＝owner / admin に限っている）。
 */
import { useEffect, useMemo, useState } from 'react'
import { supabase, type AssetField, type AssetFieldType, type CategoryField, type FieldOption } from '../../lib/supabase.ts'
import { FIELD_TYPE_LABEL, isValidFieldKey, nextOptionValue } from '../../lib/fields.ts'

const TYPES: AssetFieldType[] = ['text', 'number', 'date', 'boolean', 'enum', 'multi_enum']
const isEnumType = (t: AssetFieldType) => t === 'enum' || t === 'multi_enum'

/** 表示名が英数字だけなら key の候補にする。日本語なら空（手で付けてもらう）。 */
export function suggestFieldKey(label: string): string {
  if (!/^[A-Za-z][A-Za-z0-9 _-]*$/.test(label.trim())) return ''
  const key = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return isValidFieldKey(key) ? key : ''
}

function describeFieldError(message: string): string {
  const table: Record<string, string> = {
    field_type_immutable: '項目の型は変更できません',
    field_options_invalid: '選択肢の表示名が空です',
    field_options_duplicate: '選択肢のコードが重複しています',
  }
  for (const [code, text] of Object.entries(table)) {
    if (message.includes(code)) return `${text}（${code}）`
  }
  if (message.includes('duplicate key') || message.includes('23505')) return 'そのキーは既に使われています'
  if (message.includes('asset_fields_key_check')) return 'キーは英小文字で始まり、英小文字・数字・_ のみ（2〜63文字）にしてください'
  if (message.includes('row-level security')) return '権限がありません'
  return message
}

type Draft = {
  key: string
  label: string
  type: AssetFieldType
  description: string
  searchable: boolean
  options: FieldOption[]
}

const emptyDraft = (): Draft => ({ key: '', label: '', type: 'text', description: '', searchable: true, options: [] })

export function FieldSettings() {
  const [fields, setFields] = useState<AssetField[]>([])
  const [assignments, setAssignments] = useState<CategoryField[]>([])
  const [valueCounts, setValueCounts] = useState<Record<string, number>>({})
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [editing, setEditing] = useState<'new' | string | null>(null)

  const reload = async () => {
    const [fieldRes, assignRes, assetRes] = await Promise.all([
      supabase.from('asset_fields').select('*').order('sort_order').order('label'),
      supabase.from('category_fields').select('*'),
      supabase.from('assets').select('attributes'),
    ])
    setFields((fieldRes.data ?? []) as AssetField[])
    setAssignments((assignRes.data ?? []) as CategoryField[])
    const counts: Record<string, number> = {}
    for (const row of (assetRes.data ?? []) as { attributes: Record<string, unknown> | null }[]) {
      for (const k of Object.keys(row.attributes ?? {})) counts[k] = (counts[k] ?? 0) + 1
    }
    setValueCounts(counts)
    setLoaded(true)
  }

  useEffect(() => {
    reload()
  }, [])

  const usage = useMemo(() => {
    const m: Record<string, number> = {}
    for (const a of assignments) m[a.field_key] = (m[a.field_key] ?? 0) + 1
    return m
  }, [assignments])

  const remove = async (f: AssetField) => {
    const cats = usage[f.key] ?? 0
    const vals = valueCounts[f.key] ?? 0
    if (
      !confirm(
        `項目「${f.label}」を削除します。${cats} 件のカテゴリの割り当てが外れ、${vals} 件の素材から値が消えます。元に戻せません。よろしいですか？`,
      )
    )
      return
    setBusy(true)
    setError(null)
    setNotice(null)
    const { error } = await supabase.from('asset_fields').delete().eq('key', f.key)
    setBusy(false)
    if (error) {
      setError(`削除できませんでした: ${describeFieldError(error.message)}`)
      return
    }
    setNotice(`項目「${f.label}」を削除しました`)
    await reload()
  }

  return (
    <section className="categories">
      <p className="muted small">
        素材に付ける独自の項目（路線名・型式・撮影場所など）の台帳です。ここで作った項目を
        「カテゴリ」タブで各カテゴリに割り当てると、その配下の素材で入力・絞り込みができます。
        <strong>キーと型は作成後に変えられません</strong>（表示名と選択肢は変えられます）。
        項目の値は顧客にも表示されます。
      </p>

      <div className="cat-toolbar">
        <button type="button" disabled={busy || editing !== null} onClick={() => setEditing('new')}>
          項目を追加
        </button>
      </div>

      {editing !== null && (
        <FieldForm
          existing={editing === 'new' ? null : (fields.find((f) => f.key === editing) ?? null)}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSubmit={async (draft) => {
            setBusy(true)
            setError(null)
            setNotice(null)
            try {
              const row = {
                label: draft.label.trim(),
                description: draft.description.trim() || null,
                searchable: draft.searchable,
                options: isEnumType(draft.type) ? draft.options.map((o) => ({ value: o.value, label: o.label.trim() })) : [],
              }
              if (editing === 'new') {
                const { error } = await supabase
                  .from('asset_fields')
                  .insert({ key: draft.key.trim(), type: draft.type, sort_order: fields.length, ...row })
                if (error) throw new Error(error.message)
                setNotice(`項目「${row.label}」を追加しました`)
              } else {
                const { error } = await supabase.from('asset_fields').update(row).eq('key', editing)
                if (error) throw new Error(error.message)
                setNotice(`項目「${row.label}」を変更しました`)
              }
              setEditing(null)
              await reload()
            } catch (e) {
              setError(`保存できませんでした: ${describeFieldError(e instanceof Error ? e.message : String(e))}`)
            } finally {
              setBusy(false)
            }
          }}
        />
      )}

      {notice && <p className="success small">{notice}</p>}
      {error && <p className="error small">{error}</p>}

      <table className="invites">
        <thead>
          <tr>
            <th>表示名</th>
            <th>キー</th>
            <th>型</th>
            <th>選択肢</th>
            <th>検索</th>
            <th>使用</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.key}>
              <td className="small">
                {f.label}
                {f.description && <div className="muted small">{f.description}</div>}
              </td>
              <td className="mono small">{f.key}</td>
              <td className="small">{FIELD_TYPE_LABEL[f.type]}</td>
              <td className="small">
                {isEnumType(f.type) ? (f.options.length ? f.options.map((o) => o.label).join('、') : <span className="warn">選択肢なし</span>) : <span className="muted">—</span>}
              </td>
              <td className="small">{f.searchable ? '対象' : <span className="muted">対象外</span>}</td>
              <td className="small nowrap">
                {usage[f.key] ?? 0} カテゴリ／{valueCounts[f.key] ?? 0} 素材
              </td>
              <td>
                <div className="cat-actions">
                  <button type="button" className="small" disabled={busy || editing !== null} onClick={() => setEditing(f.key)}>
                    変更
                  </button>
                  <button type="button" className="small danger" disabled={busy || editing !== null} onClick={() => remove(f)}>
                    削除
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {loaded && fields.length === 0 && <p className="muted small">まだ項目がありません。</p>}
    </section>
  )
}

function FieldForm({
  existing,
  busy,
  onCancel,
  onSubmit,
}: {
  existing: AssetField | null
  busy: boolean
  onCancel: () => void
  onSubmit: (draft: Draft) => void
}) {
  const [draft, setDraft] = useState<Draft>(() =>
    existing
      ? {
          key: existing.key,
          label: existing.label,
          type: existing.type,
          description: existing.description ?? '',
          searchable: existing.searchable,
          options: existing.options.map((o) => ({ ...o })),
        }
      : emptyDraft(),
  )
  const [keyTouched, setKeyTouched] = useState(!!existing)
  const patch = (next: Partial<Draft>) => setDraft((d) => ({ ...d, ...next }))

  const keyOk = isValidFieldKey(draft.key)
  const optionsOk = !isEnumType(draft.type) || draft.options.every((o) => o.label.trim().length > 0)
  const canSubmit = draft.label.trim().length > 0 && keyOk && optionsOk

  return (
    <form
      className="subform invite-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (canSubmit) onSubmit(draft)
      }}
    >
      <h3>{existing ? `項目「${existing.label}」を変更` : '項目を追加'}</h3>
      <div className="row">
        <div>
          <label htmlFor="fld-label">表示名</label>
          <input
            id="fld-label"
            value={draft.label}
            onChange={(e) => {
              const label = e.target.value
              patch(keyTouched ? { label } : { label, key: suggestFieldKey(label) })
            }}
            placeholder="進行方向"
            required
            autoFocus
          />
        </div>
        <div>
          <label htmlFor="fld-key">キー（英小文字・数字・_。作成後は変更不可）</label>
          <input
            id="fld-key"
            value={draft.key}
            disabled={!!existing}
            onChange={(e) => {
              setKeyTouched(true)
              patch({ key: e.target.value })
            }}
            placeholder="direction"
            pattern="[a-z][a-z0-9_]{1,62}"
            required
          />
          {!existing && draft.key && !keyOk && (
            <p className="error small">英小文字で始まり、英小文字・数字・_ のみ（2〜63文字）</p>
          )}
        </div>
        <div>
          <label htmlFor="fld-type">型（作成後は変更不可）</label>
          <select
            id="fld-type"
            value={draft.type}
            disabled={!!existing}
            onChange={(e) => patch({ type: e.target.value as AssetFieldType })}
          >
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {FIELD_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="row">
        <div>
          <label htmlFor="fld-desc">説明（入力時のヒント。任意）</label>
          <input id="fld-desc" value={draft.description} onChange={(e) => patch({ description: e.target.value })} placeholder="列車の進行方向。上り＝東京方面" />
        </div>
      </div>
      <label className="checkbox inline-check">
        <input type="checkbox" checked={draft.searchable} onChange={(e) => patch({ searchable: e.target.checked })} />
        値を自由文検索の対象にする
      </label>

      {isEnumType(draft.type) && (
        <OptionsEditor options={draft.options} onChange={(options) => patch({ options })} />
      )}

      <div className="staff-actions">
        <button className="primary" disabled={busy || !canSubmit}>
          {busy ? '保存中…' : existing ? '変更を保存' : '項目を追加'}
        </button>
        <button type="button" className="link" disabled={busy} onClick={onCancel}>
          やめる
        </button>
      </div>
    </form>
  )
}

/**
 * 選択肢の編集。コードは自動採番で固定し、表示名だけを変えられる。
 * コードを見せるのは、API や CSV で値を扱うときに突き合わせられるようにするため。
 */
function OptionsEditor({ options, onChange }: { options: FieldOption[]; onChange: (next: FieldOption[]) => void }) {
  return (
    <div className="options-editor">
      <label>選択肢（表示名は後から変えられます。保存値はコード）</label>
      {options.map((o, i) => (
        <div className="opt-row" key={o.value}>
          <input
            value={o.label}
            onChange={(e) => onChange(options.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
            placeholder={`選択肢 ${i + 1}`}
            aria-label={`選択肢 ${i + 1} の表示名`}
          />
          <code>{o.value}</code>
          <button type="button" className="small" disabled={i === 0} aria-label="上へ" onClick={() => onChange(swap(options, i, i - 1))}>
            ↑
          </button>
          <button type="button" className="small" disabled={i === options.length - 1} aria-label="下へ" onClick={() => onChange(swap(options, i, i + 1))}>
            ↓
          </button>
          <button type="button" className="small danger" onClick={() => onChange(options.filter((_, j) => j !== i))}>
            削除
          </button>
        </div>
      ))}
      <div>
        <button type="button" className="small" onClick={() => onChange([...options, { value: nextOptionValue(options), label: '' }])}>
          選択肢を追加
        </button>
      </div>
      {options.length === 0 && <p className="warn small">選択肢が無いと値を入力できません。</p>}
    </div>
  )
}

const swap = <T,>(list: T[], i: number, j: number): T[] => {
  if (j < 0 || j >= list.length) return list
  const next = [...list]
  ;[next[i], next[j]] = [next[j]!, next[i]!]
  return next
}
